import React, { useState, useEffect, useMemo } from "react";
import classNames from "classnames";
import { MAINTANCE_TYPE_CODE } from "../../config/constants";
import ModalInfo from "../ModalInfo";
import {
  ReserveItemChildrenTypes,
  RESERVATION_DISPLAY_TYPE,
  ReservationItemTypes,
} from "../../interfaces/home";
import useGlobalInitData from "../../store/useGlobalInitData";
import { sendReport } from "../../../../utils/reporter";
import styles from "./index.module.scss";
import moment from "moment";
import { useMemoizedFn } from "ahooks";
import { isAppVersionGreaterThanOrEqualTo } from "../../../../utils/version";
import MessageBox from "../../../../components/message-box";
import jsbridge from "@xpeng/js-bridge";
import useBaseInfo from "../../store/useBaseInfo";
import { isHarmony } from "../../../../utils/common";

export interface ReservationItemsProps {
  reserveItems: ReservationItemTypes[];
  onChange?: (newReserveItems: ReservationItemTypes[]) => void;
}

export default function ReservationItems(props: ReservationItemsProps) {
  const { reserveItems = [], onChange } = props;
  const { globalInitData } = useGlobalInitData();

  const adviceInfo = globalInitData.reserveAdviceDto;
  const maintainInfo = globalInitData.reserveMaintainItemDto;
  const maintainUrl = globalInitData.maintainUrl;
  const smartMaintainActivated = globalInitData.smartMaintainActivated; // 是否开启智能保养
  const appVersion = globalInitData.appVersion;

  // 是否有保养项目
  const hasMaintainItem = useMemo(
    () =>
      smartMaintainActivated ? !!maintainUrl : !!maintainInfo?.items?.length,
    [smartMaintainActivated, maintainUrl, maintainInfo]
  );

  const supportMultipleSelect = useMemo(() => {
    return reserveItems.find(
      (item) => item.displayType === RESERVATION_DISPLAY_TYPE.NORMAL
    );
  }, [reserveItems]);

  const genAdviceText = useMemo(() => {
    let adviceTextElem = <span>请到小鹏服务中心进行专业保养</span>;

    if (!adviceInfo) {
      return <React.Fragment>{adviceTextElem}</React.Fragment>;
    }

    if (!hasMaintainItem) {
      return;
    }

    if (adviceInfo) {
      // 只比较日期部分，忽略时分秒
      const startDate = moment(moment().format("YYYY-MM-DD"));
      const endDate = moment(
        moment(adviceInfo?.nextMaintainDate).format("YYYY-MM-DD")
      );
      const datediff = endDate.diff(startDate, "days");
      console.log("datediff", datediff);

      let milediff =
        adviceInfo?.nextMaintainMileage - adviceInfo?.currentMileage;
      if (adviceInfo.currentMileage > 0 && datediff > 0 && milediff > 0) {
        adviceTextElem = (
          <span>
            距离下次保养 <span className={styles.highlight}>{milediff}</span>
            公里/
            <span className={styles.highlight}>{datediff}</span>天
          </span>
        );
      } else if (milediff <= 0) {
        adviceTextElem = (
          <span className={styles.tipsAlert}>
            已超过保养里程，建议立即到店保养
          </span>
        );
      } else if (datediff <= 0) {
        adviceTextElem = (
          <span className={styles.tipsAlert}>
            已超过保养日期，建议立即到店保养
          </span>
        );
      }
    }
    return <React.Fragment>{adviceTextElem}</React.Fragment>;
  }, [adviceInfo, hasMaintainItem]);

  // 常规预约项选中/取消选中
  const onNormalItemToggleHandler = (data) => {
    const updatedList = reserveItems.map((item) => {
      if (item.code === data.code) {
        return {
          ...item,
          ...data,
        };
      }
      return item;
    });
    onChange?.(updatedList);
  };

  // 特殊预约项选中/取消选中，只选最多选择一项
  const onSpecialItemToggleHandler = (data) => {
    const updatedList = reserveItems.map((item) => {
      if (item.displayType === RESERVATION_DISPLAY_TYPE.SPECIAL) {
        return {
          ...item,
          checked: data.checked ? item.eventCode === data.code : false,
        };
      }
      return item;
    });
    onChange?.(updatedList);
  };

  return (
    <div className={styles.serviceContainer}>
      <div className={styles.title}>
        <span>预约项目</span>
        {supportMultipleSelect ? (
          <span className={styles.tip}>可多选</span>
        ) : null}
      </div>
      <div className={styles.serviceList}>
        {reserveItems?.map((item) => {
          const code =
            item.displayType === RESERVATION_DISPLAY_TYPE.SPECIAL
              ? item.eventCode
              : item.code;
          return (
            <RadioItem
              key={code}
              title={item.name}
              desc={item.desc}
              code={code}
              checked={item.checked}
              itemDesc={
                item.code === MAINTANCE_TYPE_CODE.CHECK
                  ? genAdviceText
                  : item.desc
              }
              maintainInfoList={maintainInfo?.items}
              hasMaintainItem={hasMaintainItem}
              smartMaintainActivated={smartMaintainActivated}
              maintainUrl={maintainUrl}
              appVersion={appVersion}
              onToggle={
                item.displayType === RESERVATION_DISPLAY_TYPE.SPECIAL
                  ? onSpecialItemToggleHandler
                  : onNormalItemToggleHandler
              }
              childOptions={item.children}
            />
          );
        })}
      </div>
    </div>
  );
}

function RadioItem(props) {
  const {
    title,
    itemDesc,
    checked = false,
    code,
    onToggle,
    childOptions,
    maintainInfoList = [],
    hasMaintainItem,
    smartMaintainActivated,
    maintainUrl,
    appVersion,
  } = props;
  // 打开智能保养开关后，鸿蒙系统不展示【保养项目跳转入口】
  const isHarmonyAndActivated = useMemo(() => {
    const flag = isHarmony() && smartMaintainActivated;
    console.log(
      "isHarmony:",
      isHarmony(),
      "smartMaintainActivated:",
      smartMaintainActivated,
      "isHarmonyAndActivated:",
      flag
    );
    return flag;
  }, [smartMaintainActivated]);

  const { baseInfo } = useBaseInfo();
  const [showModal, setShowModal] = useState(false);
  const [suboptionStateList, setSuboptionStateList] =
    useState<ReserveItemChildrenTypes[]>(childOptions);

  const showProjectList = () => {
    console.log("tap to show modal...");
    setShowModal(true);
    sendReport({
      mcode: "点击查看保养项目",
      point: "P11183`B003",
    });
  };

  const isValidVersion = useMemo(
    () => isAppVersionGreaterThanOrEqualTo(appVersion),
    [appVersion]
  );

  const onMaintainItemClick = useMemoizedFn((e) => {
    e.stopPropagation();
    // 未开启，走旧逻辑
    if (!smartMaintainActivated) {
      showProjectList();
      return;
    }
    // 开启后，先判断版本，再打开h5
    if (!isValidVersion) {
      MessageBox.toast("APP版本过低，请更新版本后查看！");
    } else {
      jsbridge.callHandler("openUrl", {
        url: `${maintainUrl}?vin=${baseInfo.frameNumber}`,
      });
    }
  });

  const hideModal = () => {
    // setTimeout(() => {
    console.log("hideModal triggered....");
    setShowModal(false);
    // }, 400)
  };

  const onTapItemHandler = (e) => {
    e.stopPropagation();
    onToggle({
      code,
      checked: !checked,
      children: suboptionStateList
        ? suboptionStateList.map((item) => {
            return {
              name: item.name,
              code: item.code,
              checked: !!item.checked,
            };
          })
        : [],
    });

    sendReport({
      mcode: "预约项目点击",
      point: "P11183`B007",
      pa: `type=${title}`,
    });
  };

  useEffect(() => {
    setSuboptionStateList(childOptions);
  }, [childOptions]);

  const onSuboptionToggle = (item) => {
    const { code: targetSubCode } = item;
    setSuboptionStateList((preOptionList) => {
      const nowOptionList = preOptionList.map((preOptionItem) => {
        const { code } = preOptionItem;
        if (code === targetSubCode) {
          return {
            ...preOptionItem,
            checked: !preOptionItem.checked,
          };
        }
        return preOptionItem;
      });
      onToggle({
        code,
        checked: checked,
        children: nowOptionList
          ? nowOptionList.map((item) => {
              return {
                name: item.name,
                code: item.code,
                checked: item.checked,
              };
            })
          : [],
      });
      return nowOptionList;
    });
  };

  return (
    <div className={styles.radioItemWrapper}>
      <div className={styles.radioItemInner} onClick={onTapItemHandler}>
        <i
          className={[
            styles.radioTickIcon,
            `${checked ? styles.checked : ""}`,
          ].join(" ")}
        />
        <div className={styles.radioContentField}>
          <div className={styles.optionTitle}>{title}</div>
          {itemDesc && <div className={styles.optionDesc}>{itemDesc}</div>}
        </div>
        {Number(code) === MAINTANCE_TYPE_CODE.CHECK &&
          hasMaintainItem &&
          !isHarmonyAndActivated && (
            <div className={styles.actionField} onClick={onMaintainItemClick}>
              <span>保养项目</span>
              <i className={styles.arrowIcon} />
            </div>
          )}
      </div>
      {Number(code) === MAINTANCE_TYPE_CODE.REPAIR && checked && (
        <div className={styles.extraField}>
          <div className={styles.subOptionsWrapper}>
            {suboptionStateList &&
              suboptionStateList.map((child) => {
                return (
                  <div
                    key={child.code}
                    className={classNames(styles.subItemOption, {
                      [styles.selected]: child.checked,
                    })}
                    onClick={(e) => {
                      e.stopPropagation();
                      onSuboptionToggle(child);
                    }}
                  >
                    {child.name}
                    <div className={styles.optCheckedIcon}></div>
                  </div>
                );
              })}
          </div>
        </div>
      )}
      {Number(code) === MAINTANCE_TYPE_CODE.CHECK && showModal && (
        <ModalInfo
          className={styles.maintainItemsModal}
          showModal={showModal}
          onHide={hideModal}
          title="保养项目"
        >
          <div className={styles.maintainItemList}>
            {maintainInfoList &&
              maintainInfoList.map((infoItem, i) => {
                return (
                  <div key={i} className={styles.maintainItem}>
                    <div className={styles.leftContent}>{infoItem.item}</div>
                    <div className={styles.rightContent}>
                      x{infoItem.number}
                    </div>
                  </div>
                );
              })}
          </div>
          <div className={styles.modalTips}>
            *以上信息仅供参考，实际以门店检查为准
          </div>
        </ModalInfo>
      )}
    </div>
  );
}
